import * as React from "react";
import PropTypes from "prop-types";

import Input from "../../forms/Input";
import Tooltip from "../../forms/Tooltip";
import Button from "../../buttons/Button";
import LoadingPanel from "../../LoadingPanel";
import { getClientVersion, getURLParamsByNames } from "../../utils/helper";
import "./Registration.scss";
import { useEffect, useState } from "react";
import { useDidMount } from "../../utils/hooks";

/**
 * Registration component
 * @module
 * @param {array} additionalElements - Additional elements
 * @param {string} [emailLabel] - Email label, "Email" if not provided
 * @param {element} header - Header element
 * @param {bool} isLoading - Is loading?
 * @param {string} [passwordLabel] - Password label, "Heslo" if not provided
 * @param {string} [verifyPasswordLabel] - Verify password label, "Overenie hesla" if not provided
 * @param {string} [userNameLabel] - User name label, "Prihlasovacie meno" if not provided
 */
const Registration = (props) => {
  const { code, provider } = getURLParamsByNames(["code", "provider"]);

  const { emailValue } = props;

  const [state, setState] = useState({
    email: emailValue,
    username: '',
    password: '',
    verifyPassword: '',
  });

  useDidMount(async () => {
    if (code && provider) await props.oAuthRegister(code, provider);
  });

  useEffect(() => {
    if (emailValue && emailValue !== state.email)
      setState(prevState => ({ ...prevState, email: emailValue }))
  }, [emailValue])

  const handleChange = (name, value) => {
    setState(prevState => ({ ...prevState, [name]: value }));
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    props.onSubmit(state);
  };

  const { children } = props;

  return (
    <div className="row m-0">
      <div className="col-4 offset-4 p-0 registrationForWeb">
        <div className="card registration">
          <div className="card-body">
            <div className="card-title">
              <div>{props.header}</div>
              <div style={{ float: "right", fontSize: 12, paddingRight: 16 }}>
                {getClientVersion()}
              </div>
            </div>

            <Tooltip>
              <form className="k-form" onSubmit={handleSubmit}>
                {(props.hide === undefined ||
                  props.hide.username === undefined ||
                  props.hide.username === false) && (
                    <div className="k-form-field">
                      <span>{props.userNameLabel || "Prihlasovacie meno:"}</span>
                      <Input
                        title={props.titles && props.titles.username}
                        name="username"
                        onChange={handleChange}
                        value={state.username}
                        disabled={props.disabled && props.disabled.username}
                      />
                    </div>
                  )}
                {(props.hide === undefined ||
                  props.hide.email === undefined ||
                  props.hide.email === false) && (
                    <div className="k-form-field">
                      <span>{`*${props.emailLabel || "Email:"}`}</span>
                      <Input
                        title={props.titles && props.titles.email}
                        type="email"
                        name="email"
                        required={true}
                        onChange={handleChange}
                        value={state.email}
                        disabled={props.disabled && props.disabled.email}
                        validationMessage="Nesprávny format mailovej adresy"
                      />
                    </div>
                  )}
                {(props.hide === undefined ||
                  props.hide.password === undefined ||
                  props.hide.password === false) && (
                    <>
                      <div className="k-form-field">
                        <span>{`*${props.passwordLabel || "Heslo:"}`}</span>
                        <Input
                          title={props.titles && props.titles.password}
                          type="password"
                          name="password"
                          required={true}
                          onChange={handleChange}
                          value={state.password}
                          disabled={props.disabled && props.disabled.password}
                          pattern="(?=.*[a-z].*)(?=.*[A-Z].*)(?=.*[0-9].*)(?=.*[@$!%*?#^\+\-/.].*)^[a-zA-Z0-9@$!%*?#^\+\-/.]{8,}"
                          validationMessage="Heslo musí pozostávať aspoň z 8 znakov a musí obsahovať aspoň 
                                              jeden znak a-z, 
                                              jeden znak A-Z, 
                                              jeden znak 0-9 a 
                                              jeden zo znakov: +-*/@?!%$#^."
                        />
                      </div>
                      <div className="k-form-field">
                        <span>{`*${props.verifyPasswordLabel || "Overenie hesla:"
                          }`}</span>
                        <Input
                          title={props.titles && props.titles.verifyPassword}
                          type="password"
                          name="verifyPassword"
                          required={true}
                          onChange={handleChange}
                          value={state.verifyPassword}
                          disabled={props.disabled && props.disabled.password}
                          validationMessage="Hesla sa nezhodujú"
                          valid={state.password === state.verifyPassword}
                        />
                      </div>
                    </>
                  )}

                {props.showBackButton ? (
                  <div className="k-form-field">
                    <div className="footerContainer">
                      <Button
                        onClick={props.backButtonClick}
                        className="backButton"
                        primary={true}
                        disabled={props.isLoading}
                        label={props.backButtonLabel || "Späť"}
                      />
                      <Button
                        className="registerButton"
                        primary={true}
                        disabled={props.isLoading}
                        label={props.registerButtonLabel || "Registrovať"}
                        type="submit"
                      />
                    </div>
                  </div>
                ) : (
                  <Button
                    className="registerButton"
                    primary={true}
                    disabled={props.isLoading}
                    label={props.registerButtonLabel || "Registrovať"}
                    type="submit"
                  />
                )}

                {props.children}
              </form>
            </Tooltip>

            <LoadingPanel isLoading={props.isLoading} />
          </div>
        </div>
      </div>
      <div className="col p-0 registrationForMobile">
        <div className="card registration">
          <div className="card-body">
            <div className="card-title">{props.header}</div>
            <div style={{ float: "right", fontSize: 12, paddingRight: 16 }}>
              {getClientVersion()}
            </div>

            <Tooltip>
              <form className="k-form" onSubmit={handleSubmit}>
                {(props.hide === undefined ||
                  props.hide.username === undefined ||
                  props.hide.username === false) && (
                    <div className="k-form-field">
                      <span>{props.userNameLabel || "Prihlasovacie meno:"}</span>
                      <Input
                        title={props.titles && props.titles.username}
                        name="username"
                        onChange={handleChange}
                        value={props.username}
                        disabled={props.disabled && props.disabled.username}
                      />
                    </div>
                  )}
                {(props.hide === undefined ||
                  props.hide.email === undefined ||
                  props.hide.email === false) && (
                    <div className="k-form-field">
                      <span>{`*${props.emailLabel || "Email:"}`}</span>
                      <Input
                        title={props.titles && props.titles.email}
                        type="email"
                        name="email"
                        required={true}
                        onChange={handleChange}
                        value={state.email}
                        disabled={props.disabled && props.disabled.email}
                        validationMessage="Nesprávny format mailovej adresy"
                      />
                    </div>
                  )}
                {(props.hide === undefined ||
                  props.hide.password === undefined ||
                  props.hide.password === false) && (
                    <>
                      <div className="k-form-field">
                        <span>{`*${props.passwordLabel || "Heslo:"}`}</span>
                        <Input
                          title={props.titles && props.titles.password}
                          type="password"
                          name="password"
                          required={true}
                          onChange={handleChange}
                          value={state.password}
                          disabled={props.disabled && props.disabled.password}
                          pattern="(?=.*[a-z].*)(?=.*[A-Z].*)(?=.*[0-9].*)(?=.*[@$!%*?#^\+\-/.].*)^[a-zA-Z0-9@$!%*?#^\+\-/.]{8,}"
                          validationMessage="Heslo musí pozostávať aspoň z 8 znakov a musí obsahovať aspoň 
                                              jeden znak a-z, 
                                              jeden znak A-Z, 
                                              jeden znak 0-9 a 
                                              jeden zo znakov: +-*/@?!%$#^."
                        />
                      </div>
                      <div className="k-form-field">
                        <span>{`*${props.verifyPasswordLabel || "Overenie hesla:"
                          }`}</span>
                        <Input
                          title={props.titles && props.titles.verifyPassword}
                          type="password"
                          name="verifyPassword"
                          required={true}
                          onChange={handleChange}
                          value={state.verifyPassword}
                          disabled={props.disabled && props.disabled.password}
                          validationMessage="Hesla sa nezhodujú"
                          valid={state.password === state.verifyPassword}
                        />
                      </div>
                    </>
                  )}

                {props.showBackButton ? (
                  <div className="k-form-field">
                    <div className="footerContainer">
                      {props.showBackButton && (
                        <Button
                          onClick={props.backButtonClick}
                          className="backButton"
                          primary={true}
                          disabled={props.isLoading}
                          label={props.backButtonLabel || "Späť"}
                        />
                      )}
                      <Button
                        className="registerButton"
                        primary={true}
                        disabled={props.isLoading}
                        label={props.registerButtonLabel || "Registrovať"}
                        type="submit"
                      />
                    </div>
                  </div>
                ) : (
                  <Button
                    className="registerButton"
                    primary={true}
                    disabled={props.isLoading}
                    label={props.registerButtonLabel || "Registrovať"}
                    type="submit"
                  />
                )}

                {children}
              </form>
            </Tooltip>

            <LoadingPanel isLoading={props.isLoading} />
          </div>
        </div>
      </div>
    </div>
  );
}

Registration.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  header: PropTypes.element.isRequired,
  isLoading: PropTypes.bool.isRequired,
  registerButtonLabel: PropTypes.string,
  emailLabel: PropTypes.string,
  emailValue: PropTypes.string,
  passwordLabel: PropTypes.string,
  showUsernameInput: PropTypes.bool,
  verifyPasswordLabel: PropTypes.string,
  userNameLabel: PropTypes.string,
  disabled: PropTypes.object,
  hide: PropTypes.object,
  titles: PropTypes.object,
  oAuthRegister: PropTypes.func,
  backButtonClick: PropTypes.func,
  backButtonLabel: PropTypes.string,
  showBackButton: PropTypes.bool,
};

export default Registration;
